import { useEffect, useState } from 'react';
import {
  DevHubBox,
  DevHubCard,
  DevHubGrid,
  DevHubTabbedPage,
} from '@devxdiscover/devhub-ui';
import { UniversalWorklistTable } from './UniversalWorklistTable';
import { useLocation, useNavigate } from 'react-router-dom';
import { PrivacyWork } from './PrivacyWork';
import { EntityPluginDevxprotectExperienceCard } from '@devxprotect/plugin-devxprotect-experience';
import { MySummary } from './MySummary';
import { MyTickets } from './MyTickets';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';
import { MyIdeasView } from '../onboarding/experimentation/MyIdeasView';

/**
 * Custom ActionCenter Page Component for DevHub
 * @constructor
 */
export const ActionCenter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [numItems, setNumItems] = useState<number>(0);
  const [, setTeamTabVisible] = useState(false);
  const tabPath = location.pathname.split('/').pop();
  const uwlEnabled = ConfigHelper.instance.isFeatureEnabled(
    Feature.UNIVERSAL_WORKLIST,
  );
  const experimentationOnboardingEnabled =
    ConfigHelper.instance.isFeatureEnabled(Feature.EXPERIMENTATION_ONBOARDING);

  const tabs = [
    {
      label: 'My Summary',
      route: 'my-summary',
      content: (
        <DevHubGrid container spacing={3}>
          <DevHubGrid item xs={12}>
            <DevHubCard
              title="My Pull Requests & Incidents"
              body={
                <DevHubBox padding="medium">
                  <MySummary expanded />
                </DevHubBox>
              }
            />
          </DevHubGrid>
        </DevHubGrid>
      ),
    },
    {
      label: 'My Tickets',
      route: 'my-tickets',
      content: (
        <DevHubGrid container spacing={3}>
          <DevHubGrid item xs={12}>
            <DevHubCard
              title="My Tickets"
              body={
                <DevHubBox padding="medium">
                  <MyTickets expanded />
                </DevHubBox>
              }
            />
          </DevHubGrid>
        </DevHubGrid>
      ),
    },
    {
      label: 'My Worklist',
      route: 'worklist',
      content: (
        <DevHubGrid container spacing={3}>
          <DevHubGrid item xs={12}>
            <UniversalWorklistTable />
          </DevHubGrid>
        </DevHubGrid>
      ),
    },
    {
      label: 'My Teams',
      route: 'my-teams',
      hasPermissions: true,
      content: (
        <DevHubGrid container spacing={3}>
          <DevHubGrid item xs={12}>
            <DevHubCard
              title="My Teams"
              body={
                <DevHubBox padding="medium">
                  <EntityPluginDevxprotectExperienceCard
                    setNumItems={setNumItems}
                    setIsDataLoading={setIsDataLoading}
                    expanded
                  />
                </DevHubBox>
              }
            />
          </DevHubGrid>
        </DevHubGrid>
      ),
    },
  ];

  // Conditionally add My Ideas tab after My Worklist (index 3)
  if (experimentationOnboardingEnabled) {
    tabs.splice(3, 0, {
      label: 'My Ideas',
      route: 'my-ideas',
      content: (
        <DevHubGrid container spacing={3}>
          <DevHubGrid item xs={12}>
            <MyIdeasView />
          </DevHubGrid>
        </DevHubGrid>
      ),
    });
  }

  if (!uwlEnabled) {
    tabs.splice(4, 0, {
      label: 'Privacy',
      route: 'privacy',
      content: <PrivacyWork />,
    });
  }

  useEffect(() => {
    if (!isDataLoading) {
      if (numItems > 0) {
        setTeamTabVisible(true);
      } else {
        setTeamTabVisible(false);
      }
    }
  }, [numItems, setNumItems, isDataLoading]);

  const getTabIndex = (path: string | undefined) => {
    const tabIndex = tabs.findIndex(tab => tab.route === path);
    return tabIndex >= 0 ? tabIndex : 0;
  };

  const initialTab = getTabIndex(tabPath);
  const [currentTab, setCurrentTab] = useState(initialTab);

  useEffect(() => {
    const newIndex = getTabIndex(tabPath);
    if (newIndex !== currentTab) {
      setCurrentTab(newIndex);
    } else if (!tabPath) {
      navigate(tabs[0].route, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabPath, navigate, currentTab, tabs]);

  return (
    <DevHubTabbedPage
      title="Action Center"
      subtitle="Manage your actions and your assigned tasks."
      tabs={tabs}
      navigate={navigate}
      currentTab={currentTab}
    />
  );
};
